// Reward eligibility computed from existing on-chain data — fast, scalable.
// Tier 0 (Android Tab):       5 directs in $1000 package
// Tier 1 (Foreign Trip):      25 members at L2 in $1000 package matrix
// Tier 2 ($1200 Fund):       125 members at L3 in $1000 package matrix
// Tier 3 ($6000 Bike):       625 members at L4 in $1000 package matrix
// Tier 4 ($36000 House):    3125 members at L5 in $1000 package matrix
//
// Eligible users are cached in localStorage so admin can view the list.
// Admin manually verifies and marks reward given via contract.

const TIER_REQUIREMENTS = [
  { directs: 5 },                  // Tier 0 — direct refs in pkg 6
  { matrixLevel: 2, count: 25 },   // Tier 1
  { matrixLevel: 3, count: 125 },  // Tier 2
  { matrixLevel: 4, count: 625 },  // Tier 3
  { matrixLevel: 5, count: 3125 }  // Tier 4
];

const STORAGE_KEY = 'uvw_reward_eligible_users';

// Returns user's eligibility for all tiers using just 2 contract reads:
//   1. getActiveDirectsPerPackage(userId, 6)
//   2. getAutoLevelCountAllLevels(userId, 6)
// Plus a check that the user has $1000 package active.
export async function checkUserAllTiers(readContracts, userId) {
  try {
    const [hasPkg6, directsInPkg6, levelCounts] = await Promise.all([
      readContracts.core.isUserPackageActive(userId, 6).catch(() => false),
      readContracts.core.getActiveDirectsPerPackage(userId, 6).catch(() => null),
      readContracts.pools.getAutoLevelCountAllLevels(userId, 6).catch(() => null)
    ]);

    if (!hasPkg6) {
      return TIER_REQUIREMENTS.map(req => ({
        eligible: false,
        count: 0,
        required: req.directs || req.count
      }));
    }

    const directs = directsInPkg6 ? (directsInPkg6.toNumber ? directsInPkg6.toNumber() : Number(directsInPkg6)) : 0;
    const counts = levelCounts && Array.isArray(levelCounts)
      ? levelCounts.map(c => c.toNumber ? c.toNumber() : Number(c))
      : Array(10).fill(0);

    return TIER_REQUIREMENTS.map((req, i) => {
      if (i === 0) {
        return { eligible: directs >= req.directs, count: directs, required: req.directs };
      }
      const cnt = counts[req.matrixLevel - 1] || 0;
      return { eligible: cnt >= req.count, count: cnt, required: req.count };
    });
  } catch {
    return TIER_REQUIREMENTS.map(req => ({ eligible: false, count: 0, required: req.directs || req.count }));
  }
}

// Save user's eligibility to localStorage (per tier)
// Structure: { tierIdx: { userId: { count, required, ts } } }
export function saveEligibility(userId, tierResults) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : {};
    let changed = false;
    for (let i = 0; i < tierResults.length; i++) {
      const tier = tierResults[i];
      if (!data[i]) data[i] = {};
      if (tier.eligible) {
        if (!data[i][userId] || data[i][userId].count !== tier.count) {
          data[i][userId] = { count: tier.count, required: tier.required, ts: Date.now() };
          changed = true;
        }
      } else {
        // If was eligible but no longer (e.g. ceiling deactivation), remove
        if (data[i][userId]) {
          delete data[i][userId];
          changed = true;
        }
      }
    }
    if (changed) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* silent */ }
}

// Get list of eligible users for a tier (from localStorage)
export function getEligibleList(tierIdx) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    const tierData = data[tierIdx] || {};
    return Object.entries(tierData).map(([uid, info]) => ({
      userId: parseInt(uid),
      count: info.count,
      required: info.required,
      ts: info.ts
    })).sort((a, b) => b.count - a.count);
  } catch { return []; }
}

// Clear stored eligibility (admin action)
export function clearEligibility() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
}

// Remove a specific user from a tier (e.g. when reward marked given)
export function removeUserFromTier(tierIdx, userId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (data[tierIdx] && data[tierIdx][userId]) {
      delete data[tierIdx][userId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch { /* silent */ }
}

export const TIER_INFO = TIER_REQUIREMENTS;
